import { CrudPage } from '../../components/CrudPage';
import { type MaterialDto, MaterialControllerService } from '../../api';
import ArticleFormDialog from '../forms/ArticleFormDialog';
import { describeArticleProperties } from '../forms/articleFormSchema';
import { useTranslation } from 'react-i18next';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';

// Type with required id for the CrudPage
type MaterialWithId = MaterialDto & { id: string };

const IntermediateProductsPage = () => {
  const { t } = useTranslation();

  const formatBaseUnit = (data: any) => {
    const value = data.baseUnit;
    if (!value) return '';
    
    // Map backend enum values to translation keys
    const unitMap: { [key: string]: string } = {
      'COUNT': 'piece',
      'GRAM': 'gram',
      'KILOGRAM': 'kilogram',
      'MILLIGRAM': 'milligram',
      'TON': 'ton',
      'LITER': 'liter',
      'MILLILITER': 'milliliter',
      'CUBIC_METER': 'cubicMeter',
      'METER': 'meter',
      'CENTIMETER': 'centimeter',
      'MILLIMETER': 'millimeter',
      'KILOMETER': 'kilometer',
      'SQUARE_METER': 'squareMeter',
      'SQUARE_CENTIMETER': 'squareCentimeter',
      'SQUARE_MILLIMETER': 'squareMillimeter'
    };
    
    const translationKey = unitMap[value] || value.toLowerCase();
    return t(`form.baseUnit.${translationKey}`);
  };

  // Checkbox template for global field
  const GlobalCheckboxTemplate = (data: any) => {
    return (
      <div className="flex justify-center">
        <CheckBoxComponent
          checked={data.global}
          disabled={true}
          cssClass="e-success"
        />
      </div>
    );
  };

  const formatPlanningMode = (data: any) => {
    const material = data?.data || data?.rowData || data;
    const value = material?.planningMode;
    if (value === 'AUTO') return t('intermediateProduct.planningModeAuto', { defaultValue: 'Automatisch' });
    if (value === 'MANUAL') return t('intermediateProduct.planningModeManual', { defaultValue: 'Manuell' });
    return '';
  };

  const formatNumberOrDash = (value: unknown, suffix = '') => {
    if (value === null || value === undefined || value === '') return '-';
    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(n)) return '-';
    return `${n}${suffix ? ' ' + suffix : ''}`;
  };

  const formatLotRow = (data: any) => {
    const m = data?.data || data?.rowData || data;
    return formatNumberOrDash(m?.minLotSize);
  };
  const formatMaxLotRow = (data: any) => {
    const m = data?.data || data?.rowData || data;
    return formatNumberOrDash(m?.maxLotSize);
  };
  const formatCoverageRow = (data: any) => {
    const m = data?.data || data?.rowData || data;
    return formatNumberOrDash(m?.minCoverageDays, t('intermediateProduct.daysShort', { defaultValue: 'd' }));
  };
  const formatLeadTimeRow = (data: any) => {
    const m = data?.data || data?.rowData || data;
    return formatNumberOrDash(m?.leadTimeDays, t('intermediateProduct.daysShort', { defaultValue: 'd' }));
  };

  const formatStorageRow = (data: any) => {
    const m = data?.data || data?.rowData || data;
    return formatNumberOrDash(m?.maxStorageDays, t('intermediateProduct.daysShort', { defaultValue: 'd' }));
  };

  const gridColumns = [
    { field: 'name', headerKey: 'grid.name', width: 180 },
    { field: 'articleNumber', headerKey: 'material.articleNumber', width: 120 },
    { field: 'description', headerKey: 'grid.description', width: 200 },
    { field: 'baseUnit', headerKey: 'form.baseUnit', width: 120, render: formatBaseUnit },
    { field: 'propertiesText', headerKey: 'article.properties', width: 220 },
    { field: 'maxStorageDays', headerKey: 'article.maxStorageDays', width: 130, textAlign: 'Right', render: formatStorageRow },
    { field: 'planningMode', headerKey: 'intermediateProduct.planningMode', width: 140, render: formatPlanningMode },
    { field: 'minLotSize', headerKey: 'intermediateProduct.minLotSize', width: 120, textAlign: 'Right', render: formatLotRow },
    { field: 'maxLotSize', headerKey: 'intermediateProduct.maxLotSize', width: 120, textAlign: 'Right', render: formatMaxLotRow },
    { field: 'minCoverageDays', headerKey: 'intermediateProduct.minCoverageDays', width: 140, textAlign: 'Right', render: formatCoverageRow },
    { field: 'leadTimeDays', headerKey: 'intermediateProduct.leadTimeDays', width: 120, textAlign: 'Right', render: formatLeadTimeRow },
    { field: 'global', headerKey: 'form.global', width: 100, render: GlobalCheckboxTemplate }
  ];

  return (
    <CrudPage<MaterialWithId>
      titleKey="routes.intermediateProducts"
      gridMeta={gridColumns}
      fetchData={async () => {
        const materials = await MaterialControllerService.findAll();
        const nameByUuid = new Map(materials.map(material => [material.uuid, material.name]));
        return materials
          .filter(material => material.materialType === 'INTERMEDIATE_PRODUCT')
          .map(material => ({
            ...material,
            id: material.uuid || '',
            propertiesText: describeArticleProperties(material, t as never, nameByUuid),
          }));
      }}
      saveData={async (data: MaterialWithId) => {
        // Ensure materialType is set to INTERMEDIATE_PRODUCT
        const intermediateProductData = { ...data, materialType: 'INTERMEDIATE_PRODUCT' as const };

        if (intermediateProductData.id) {
          await MaterialControllerService.update(intermediateProductData.id, intermediateProductData);
        } else {
          await MaterialControllerService.create(intermediateProductData);
        }
      }}
      deleteData={MaterialControllerService.delete}
      FormComponent={(props) => <ArticleFormDialog {...props} materialType="INTERMEDIATE_PRODUCT" />}
      entityName="material"
    />
  );
};

export default IntermediateProductsPage;
